import {CallState} from './enums/call-state.enum';
import {Call, QueuedCall} from '@gt-api';
import {AnsweredCall, CreateCall, Enqueue, EUAnsweredCall, EUCreateCall, TransferCall} from '@app/services/push-event/push-events';
import {RedirectEntry} from '../shared/utils/redirect-entry';
import {ContactModel} from './contact.model';

export class CallModel {
  duration = 0;
  caller: ContactModel = null;
  redirectEntries: RedirectEntry[];
  lookupDone = false;
  isPrivateCall = false;
  interactionDisabled = false;

  get sessionId(): number {
    return this.call.sessionId;
  }

  get serviceNumber(): string {
    return '' + this.call.serviceNumber;
  }

  get componentId(): number {
    return this.call.componentId;
  }

  get state(): number {
    return this.call.state;
  }

  set state(state: number) {
    this.call.state = state;
  }

  get creationTime(): Date {
    return this.call.creationTime;
  }

  set creationTime(creationTime: Date) {
    this.call.creationTime = creationTime;
  }

  set genericNumber(genericNumber: string) {
    this.call.genericNumber = genericNumber;
  }

  get fullNumber(): string {
    return this.call.genericNumber && this.call.genericNumber !== 'null' ? this.call.genericNumber : this.call.fullNumber;
  }

  get answeringNumber(): string {
    return '' + this.call.answeringNumber;
  }

  set answeringNumber(answeringNumber: string) {
    this.call.answeringNumber = answeringNumber;
  }

  get clir(): boolean {
    return this.call.clir;
  }

  get organization(): string {
    return this.call.organization;
  }

  get agentLogin(): string {
    return this.call.agentLogin;
  }

  set agentLogin(agentLogin: string) {
    this.call.agentLogin = agentLogin;
  }

  get transferState(): number {
    return this.call.transferState;
  }

  set transferState(state: number) {
    this.call.transferState = state;
  }

  get triggerPoint(): number {
    return this.call.triggerPoint;
  }

  get cid(): number {
    return this.call.componentId;
  }

  constructor(public call: Call) {
    if (call.creationTime) {
      const now = new Date();
      const creationTime = new Date(call.creationTime); // Fix backend type issue where date is a number
      if (creationTime.getTime() > now.getTime()) {
        call.creationTime = now;
      } else {
        call.creationTime = creationTime;
      }
    }
    if (call.triggerPoint > 0) {
      this.isPrivateCall = true;
    }
    this.redirectEntries = RedirectEntry.parse(this.call.redirectingNumber);
  }

  static fromCreateCallEvent(event: CreateCall): CallModel {
    const call: Call = {
      sessionId: event.session_id,
      organization: event.oname,
      fullNumber: '' + event.a_number,
      creationTime: null,
      genericNumber: '' + event.g_number,
      redirectingNumber: event.redir_number,
      serviceNumber: '' + event.sno,
      componentId: event.cid,
      answeringNumber: '' + event.agent_number,
      agentLogin: '' + event.agent_login,
      state: CallState.Calling
    };
    return new CallModel(call);
  }

  static fromTransferCallEvent(event: TransferCall) {
    const call: Call = {
      sessionId: event.session_id,
      organization: event.oname,
      fullNumber: '' + event.transferToNumber,
      creationTime: null,
      genericNumber: '' + event.g_number,
      redirectingNumber: null,
      serviceNumber: '' + event.sno,
      componentId: event.cid,
      answeringNumber: '' + event.agent_number,
      agentLogin: '' + event.agent_login,
      state: CallState.Calling
    };
    return new CallModel(call);
  }

  static fromEnqueueEvent(event: Enqueue) {
    const call: Call = {
      sessionId: event.session_id,
      organization: event.oname,
      fullNumber: '' + event.ano,
      creationTime: new Date(event.creation_time),
      genericNumber: event.gno,
      redirectingNumber: null,
      serviceNumber: '' + event.bno,
      componentId: event.cid,
      answeringNumber: null,
      agentLogin: null,
      state: CallState.OnHold
    };
    return new CallModel(call);
  }

  static fromQueuedCall(organization: string, queuedCall: QueuedCall) {
    const call: Call = {
      sessionId: queuedCall.sessionId,
      organization,
      fullNumber: queuedCall.callerNumber,
      creationTime: queuedCall.creationTime,
      genericNumber: queuedCall.genericNumber,
      redirectingNumber: queuedCall.redirNumber,
      serviceNumber: '' + queuedCall.serviceNumber,
      componentId: queuedCall.componentId,
      answeringNumber: null,
      agentLogin: null,
      clir: queuedCall.clir,
      state: CallState.OnHold
    };
    return new CallModel(call);
  }

  static fromAnsweredCallEvent(event: AnsweredCall) {
    const call: Call = {
      sessionId: event.session_id,
      organization: event.oname,
      fullNumber: '' + event.a_number,
      creationTime: null,
      genericNumber: '' + event.g_number,
      redirectingNumber: '' + event.redir_number,
      serviceNumber: '' + event.sno,
      componentId: event.cid,
      answeringNumber: '' + event.agent_number,
      agentLogin: '' + event.agent_login,
      state: CallState.Connected
    };
    return new CallModel(call);
  }

  static fromEUAnsweredCall(event: EUAnsweredCall) {
    const call: Call = {
      sessionId: event.session_id,
      organization: event.oname,
      fullNumber: '' + event.full_number,
      creationTime: null,
      genericNumber: '' + event.g_number,
      triggerPoint: event.trigger_point,
      redirectingNumber: event.redir_number,
      serviceNumber: null,
      componentId: null,
      answeringNumber: null,
      agentLogin: null,
      state: CallState.Connected
    };
    return new CallModel(call);
  }

  static fromEUCreateCall(event: EUCreateCall) {
    const call: Call = {
      sessionId: event.session_id,
      organization: event.oname,
      fullNumber: '' + event.full_number,
      creationTime: null,
      genericNumber: '' + event.g_number,
      redirectingNumber: event.redir_number,
      serviceNumber: null,
      triggerPoint: event.trigger_point,
      componentId: null,
      answeringNumber: null,
      agentLogin: null,
      state: CallState.Calling
    };
    return new CallModel(call);
  }

  toString() {
    return `{ callId ${this.call.sessionId} state ${this.call.state} triggerPoint ${this.call.triggerPoint} isPrivateCall ${this.isPrivateCall} }`;
  }
}
